import { useEffect } from "react";

import { Divider } from "@vericus/cadmus-ui";

import { useAppDispatch } from "@/data/hooks";
import { updateSocraticSessionTimeline } from "@/features/socratic";
import { useRecordingStartCutoff } from "@/features/socratic/hooks";
import { useCreateDialogue } from "@/features/socratic/hooks/use-create-dialogue";
import { useSocraticSessionQuery } from "@/generated/graphql";

import { attemptsRemaining } from "../../helpers/attempts-remaining";
import { useLatestDialogue } from "../../helpers/get-latest-dialogue";
import { hasCompletedResponse } from "../../helpers/has-dialogue-response";
import { latestRecordingCompletedAt } from "../../helpers/latest-recording-completed-at";
import {
  ResponseRequirements,
  ResponseRequirementsProps,
} from "./response-requirements";
import { SocraticAttemptActions } from "./socratic-attempt-actions";
import * as styles from "./socratic-session-entry.css";
import { StartDialogueCTASkeleton } from "./start-dialogue-cta";

interface Props {
  answerBlockId: string;
  workId: string;
  responseRequirements: Omit<
    ResponseRequirementsProps,
    | "studentMaxAttempts"
    | "studentResponseTimeSeconds"
    | "studentPreparationTimeSeconds"
    | "hasSubmittedResponse"
    | "attemptsRemaining"
    | "reattemptWindowSeconds"
  >;
  isReadonly?: boolean;
}

/**
 * Component manages attempt state (start / re-attempt) for a Socratic
 * interaction per AnswerBlock.
 */
export function SocraticSessionEntry(props: Props) {
  const dispatch = useAppDispatch();

  const { data, loading } = useSocraticSessionQuery({
    variables: {
      answerBlockId: props.answerBlockId,
      workId: props.workId,
    },
  });
  const session = data?.socraticSession;
  const createDialogue = useCreateDialogue();
  const latestDialogue = useLatestDialogue(session?.dialogues ?? []);

  const recordingStartCutoff = useRecordingStartCutoff(
    session?.readingTimeSeconds ?? null,
    session?.responseTimeSeconds ?? null
  );

  const numAttemptsRemaining = attemptsRemaining({
    latestDialogue,
    maxAttempts: session?.maxAttempts ?? 0,
    createMetadata: createDialogue.createMetadata,
  });

  useEffect(() => {
    if (!session) return;
    dispatch(
      updateSocraticSessionTimeline({
        latestRecordingCompletedAt: latestRecordingCompletedAt(
          session.dialogues
        ),
        reattemptWindowSeconds: session.reattemptWindowSeconds,
        windowEndBeforeAnyRecording: session.reattemptWindowEndsAt,
        recordingStartCutoffAt: recordingStartCutoff?.toISOString() ?? null,
      })
    );
  }, [dispatch, session, recordingStartCutoff]);

  if (loading || !session) return <StartDialogueCTASkeleton />;

  return (
    <div className={styles.root}>
      <ResponseRequirements
        {...props.responseRequirements}
        studentMaxAttempts={session.maxAttempts}
        studentResponseTimeSeconds={session.responseTimeSeconds}
        studentPreparationTimeSeconds={session.readingTimeSeconds}
        attemptsRemaining={numAttemptsRemaining}
        reattemptWindowSeconds={session.reattemptWindowSeconds}
        hasSubmittedResponse={hasCompletedResponse(session.dialogues)}
      />
      <Divider />
      <SocraticAttemptActions
        session={session}
        workId={props.workId}
        answerBlockId={props.answerBlockId}
        isReadonly={props.isReadonly}
        hasPreparationTime={(session.readingTimeSeconds ?? 0) > 0}
        numAttemptsRemaining={numAttemptsRemaining}
        createDialogue={createDialogue}
      />
    </div>
  );
}
